import { useState } from 'react';
import { Section } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  CodeBlock,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { ConfirmDialog, useToast, Alert } from '@vnetwork/ui/feedback';
import { Copy, KeyRound, Code, Link2, Terminal } from 'lucide-react';
import { useApiKeys, useResolveApiKey, useRevokeApiKey, type ApiKey } from '@/api/api-keys';
import { brand } from '@/config/brand';
import { formatDate } from '@/lib/format-date';
import { CreateApiKeyDialog } from './CreateApiKeyDialog';
import styles from './Apikeys.module.css';

const CURL_EXAMPLE = `curl -X GET ${brand.urls.openapi}/v1/instances \\
  -H "Authorization: Bearer YOUR_API_KEY_HERE" \\
  -H "Content-Type: application/json"`;

export function ApiKeysPage() {
  const { show } = useToast();
  const { data: keys, isLoading } = useApiKeys();
  const [createOpen, setCreateOpen] = useState(false);
  const [newKey, setNewKey] = useState<{ key: string; name: string } | null>(null);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);

  const resolve = useResolveApiKey();
  const revoke = useRevokeApiKey({
    onSuccess: () => {
      show({ tone: 'success', title: 'API key revoked' });
      setRevoking(null);
    },
    onError: () => show({ tone: 'danger', title: 'Could not revoke key' }),
  });

  async function copyKey(id: string) {
    try {
      const full = await resolve.mutateAsync(id);
      await navigator.clipboard.writeText(full);
      show({ tone: 'success', title: 'API key copied to clipboard' });
    } catch {
      show({ tone: 'danger', title: 'Could not copy key' });
    }
  }

  const list = keys ?? [];

  return (
    <Section title="API Keys" description={`Programmatic access to your ${brand.shortName} resources.`}>
      {newKey && (
        <Alert tone="success" title="New API key created" style={{ marginBottom: 'var(--vn-space-4)' }}>
          <p style={{ margin: '0 0 8px' }}>
            Copy <strong>{newKey.name}</strong> now — you won't be able to see it again.
          </p>
          <CodeBlock code={newKey.key} wrap />
        </Alert>
      )}

      <Card>
        <CardHeader
          actions={<Button size="sm" onClick={() => setCreateOpen(true)}>Create new key</Button>}
        >
          <CardTitle>Your API keys</CardTitle>
          <CardDescription>
            Keys provide full programmatic access — store them securely and never commit them.
          </CardDescription>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <Skeleton style={{ height: 72 }} />
          ) : list.length === 0 ? (
            <EmptyState
              icon={<KeyRound size={20} />}
              title="No API keys yet"
              description={`Create a key to start using the ${brand.shortName} OpenAPI.`}
            />
          ) : (
            <div className={styles.list}>
              {list.map((k) => (
                <div key={k.id} className={styles.row}>
                  <div className={styles.rowText}>
                    <span className={styles.rowName}>{k.name}</span>
                    <span className={styles.rowMeta}>
                      Created {formatDate(k.created_at)} · Last used{' '}
                      {k.issued_at ? formatDate(k.issued_at) : 'never'}
                    </span>
                  </div>
                  <div className={styles.rowActions}>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => copyKey(k.id)}
                      disabled={resolve.isPending}
                    >
                      <Copy size={14} /> Copy
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => setRevoking(k)}>
                      Revoke
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      <Card style={{ marginTop: 'var(--vn-space-5)' }}>
        <CardHeader>
          <CardTitle>API documentation</CardTitle>
          <CardDescription>Learn how to use the {brand.shortName} OpenAPI.</CardDescription>
        </CardHeader>
        <CardBody>
          <div className={styles.docs}>
            <div className={styles.docBlock}>
              <span className={styles.docLabel}>
                <Code size={15} /> Authorization
              </span>
              <p className={styles.docText}>
                Send your key as a bearer token: <code>Authorization: Bearer YOUR_API_KEY</code>
              </p>
            </div>

            <div className={styles.docBlock}>
              <span className={styles.docLabel}>
                <Link2 size={15} /> Base URL
              </span>
              <p className={styles.docText}>
                <code>{brand.urls.openapi}/v1</code> — e.g.{' '}
                <code>/v1/instances</code> lists your instances.
              </p>
            </div>

            <div className={styles.docBlock}>
              <span className={styles.docLabel}>
                <Terminal size={15} /> Quick test
              </span>
              <CodeBlock code={CURL_EXAMPLE} wrap />
            </div>

            <a
              className={styles.docLink}
              href={brand.urls.postman}
              target="_blank"
              rel="noopener noreferrer"
            >
              View the full API collection on Postman →
            </a>
          </div>
        </CardBody>
      </Card>

      <CreateApiKeyDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(key, name) => {
          if (key) {
            setNewKey({ key, name });
          } else {
            // Secret wasn't in the create response — key still exists; user can
            // reveal it with the row's Copy action.
            show({
              tone: 'success',
              title: `API key “${name}” created`,
              description: 'Use Copy on the key below to reveal its secret.',
            });
          }
        }}
      />

      <ConfirmDialog
        open={!!revoking}
        onOpenChange={(o) => !o && setRevoking(null)}
        title="Revoke API key?"
        description={`“${revoking?.name}” will stop working immediately. This cannot be undone.`}
        confirmLabel="Revoke"
        destructive
        loading={revoke.isPending}
        onConfirm={() => revoking && revoke.mutate(revoking.id)}
      />
    </Section>
  );
}
